/* ============================================================
   VEYLA | listicle D, "the Korean scalp reset"

   A pure advertorial. It argues, it proves, and it routes: every CTA leaves for
   the product page. There is no cart on it and, since v6, no interactive block
   either.

   v3 removed the buybox and the cart drawer, on Omar's call, because the
   competitor lander this answers routes rather than sells. v6 removed the reset
   schedule section that had survived that cut. So what this file does now is
   the proof layer, the comment wall, two small body widgets, and the four
   figures the page still speaks.

   PRICES. Any figure this page still shows comes from ../../shared/vy-offer.js,
   and the page sets window.VY_SURFACE = 'copy' before loading it so those
   figures are the ones the destination actually charges. Nothing is hardcoded,
   so a repricing moves this page with the other surfaces.

   PROOF. Every invented element comes from vk-fiction.js and nothing else.
   This file reads VK_FICTION.live and never the raw branches, so the OFF
   switch cannot be half applied. If the fiction file is missing the page still
   renders: the proof blocks hide themselves rather than printing "undefined".
   ============================================================ */
(() => {
  const O = window.VY_OFFER;
  if (!O) return;
  const F = (window.VK_FICTION && window.VK_FICTION.live) || null;

  const $  = s => document.querySelector(s);
  const $$ = s => Array.prototype.slice.call(document.querySelectorAll(s));
  const money = O.money;
  const esc = s => String(s).replace(/[&<>"]/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* Images this file applies rather than the markup: the author's face and
     portrait, and the nineteen comment avatars. On the Shopify theme they live
     in the flat assets/ folder under a vk- prefix and are addressed by CDN url,
     so the layout emits a map from local filename to that url. Off the theme
     the map is absent and the relative path in the register is already right.
     Markup images are rewritten at build time and never come through here. */
  const img = p => (window.VK_IMG && window.VK_IMG[String(p).split('/').pop()]) || p;

  /* ============================================================
     PART ONE: the proof layer
     ============================================================ */

  const setF = (key, value) => $$('[data-f="' + key + '"]').forEach(el => { el.textContent = value; });

  const paintFiction = () => {
    if (!F) return;
    const a = F.author;
    setF('author-name', a.name);
    setF('author-role', a.role);
    setF('author-cred', a.cred);
    setF('author-bio',  a.bio);
    setF('author-lede', a.lede);
    setF('author-sign', a.sign);
    /* Initials are the fallback, and they stay the fallback. The register
       decides whether there is a face to show, because whether a face is
       allowed depends on whose face it is, and that judgement belongs in the
       one file that documents it. Every portrait in the shared folder is a
       picture of a real customer, so none of those may land here. */
    /* Whitespace only. Splitting on the hyphen as well broke a hyphenated
       given name into two words and took its initial twice. */
    setF('author-mono', a.name.replace(/^Dr\.?\s*/i, '')
      .split(/\s+/).filter(Boolean).slice(0, 2)
      .map(x => x.charAt(0).toUpperCase()).join(''));
    if (!a.cred) $('#vk-author').classList.add('is-plain');

    if (a.avatar) {
      const face = $('#vk-author-face');
      face.src = img(a.avatar);
      face.alt = a.name + ', ' + a.role;
      face.hidden = false;
      $('#vk-author').classList.add('has-face');
    }

    if (a.portrait) {
      const fig = $('#vk-hero-figure');
      const im = fig.querySelector('img');
      im.src = img(a.portrait);
      im.alt = a.name + ', ' + a.role;
      im.width = 1178; im.height = 1470;
      fig.querySelector('figcaption').textContent = a.name + '. ' + a.role + '.';
      fig.hidden = false;
    }

    const s = F.stat;
    setF('stat-value', s.value);
    setF('stat-claim', s.claim);
    setF('stat-body',  s.body);

    /* The rating strips only exist when there are numbers to put in them.
       An empty strip is worse than no strip. */
    if (F.social) {
      setF('rating', F.social.rating);
      setF('women',  F.social.women);
      const m = String(F.social.badge).match(/^(\S+)\s*(.*)$/);
      setF('badge-n', m ? m[1] : F.social.badge);
      setF('badge-t', m ? m[2] : '');
      $$('[data-strip]').forEach(el => { el.hidden = false; });
    }

    /* The refund rate appears in two registers, short inside a list item and
       long under the guarantee. Both vanish together when there is no figure. */
    if (F.refundRate) {
      setF('refund', 'Fewer than ' + F.refundRate + ' of orders come back.');
      setF('refund-long', 'Refund rate across every order to date: ' + F.refundRate + '.');
    } else {
      $$('[data-f="refund"], [data-f="refund-long"]').forEach(el => el.remove());
    }

    /* Testimonials. The real set is five and two of them are four star, so the
       foot note changes with the set rather than claiming something untrue. */
    const t = F.testimonials || [];
    const stars = n => '<span class="vk-stars" aria-label="' + n + ' out of 5">' +
      '★'.repeat(n) + '<i>' + '★'.repeat(5 - n) + '</i></span>';
    const initials = s => s.replace(/,.*$/, '').split(/[\s.]+/)
      .filter(Boolean).slice(0, 2).map(x => x.charAt(0).toUpperCase()).join('');
    $('#vk-tmls').innerHTML = t.map(x => `
      <figure class="vk-tml">
        ${stars(x.stars || 5)}
        <p class="vk-tml-head">${esc(x.head)}</p>
        <blockquote>${esc(x.body)}</blockquote>
        <figcaption class="vk-tml-who">
          ${x.avatar
            ? `<img class="vk-tml-face" src="${esc(img(x.avatar))}" alt="" loading="lazy">`
            : `<span class="vk-tml-mono" aria-hidden="true">${esc(initials(x.name))}</span>`}
          <span class="vk-tml-id"><b>${esc(x.name)}</b><small>${esc(x.tag)}</small></span>
        </figcaption>
      </figure>`).join('');
    setF('tml-foot', F.invented
      ? ''
      : 'Five reviews from the store, reproduced exactly as written, two of them four star. A page that quotes only five stars is telling you about the page.');
    if (F.invented) $$('[data-f="tml-foot"]').forEach(el => el.remove());

    /* The disclosure line in the footer is the one place the page admits, in
       its own markup, that the proof layer above is not sourced. It only
       prints when the fiction is switched on, because that is the only time
       it is true. */
    const fn = $('#vk-foot-fiction');
    if (fn && F.invented) {
      fn.hidden = false;
      fn.textContent = 'Reviewer names, comments and figures on this page are illustrative and were not supplied by the individuals named.';
    }
  };

  /* ---------- the comment wall ----------
     Relative timestamps are computed at load rather than written down, so the
     wall never reads "51 min" on a page that has been up for a month. */
  const ago = mins => {
    if (mins < 60) return Math.max(1, Math.round(mins)) + ' min';
    if (mins < 60 * 24) return Math.round(mins / 60) + ' h';
    return Math.round(mins / (60 * 24)) + ' d';
  };

  const SHOWN_FIRST = 8;

  /* Whether a commenter gets a face is the register's decision, not this
     file's, for the same reason the byline chip works that way: whether a face
     is allowed depends on whose face it is. No entry means the initial. */
  const face = name => (F && F.faces && F.faces[name]) || '';

  const commentHTML = (c, isReply) => `
    <div class="vk-cm${isReply ? ' vk-cm-reply' : ''}">
      <div class="vk-cm-av" aria-hidden="true">${face(c.name)
        ? `<img src="${esc(img(face(c.name)))}" alt="" loading="lazy">`
        : esc(c.name.charAt(0))}</div>
      <div class="vk-cm-body">
        <div class="vk-cm-bubble">
          <b>${esc(c.name)}</b>
          <p>${esc(c.body)}</p>
        </div>
        <div class="vk-cm-meta">
          <span>Like</span><span>Reply</span>
          <i class="vk-cm-likes">${c.likes}</i>
          <time>${ago(c.mins)}</time>
        </div>
        ${(c.replies || []).map(r => commentHTML(r, true)).join('')}
      </div>
    </div>`;

  const paintComments = () => {
    if (!F || !F.comments || !F.comments.length) return;
    const sec = $('#vk-comments');
    sec.hidden = false;

    const total = F.comments.reduce((n, c) => n + 1 + ((c.replies || []).length), 0);
    $('#vk-cm-count').textContent = total + ' comments';

    $('#vk-cm-list').innerHTML = F.comments.map((c, i) =>
      `<div class="vk-cm-slot${i >= SHOWN_FIRST ? ' is-hidden' : ''}">${commentHTML(c, false)}</div>`).join('');

    const more = $('#vk-cm-more');
    const hidden = () => $$('#vk-cm-list .vk-cm-slot.is-hidden');
    if (!hidden().length) { more.hidden = true; return; }
    more.textContent = 'Load ' + hidden().length + ' more comments';
    more.addEventListener('click', () => {
      hidden().forEach(el => el.classList.remove('is-hidden'));
      more.hidden = true;
    });
  };

  /* ============================================================
     PART TWO: the offer, which this page reads but never sells

     v6, 2026-08-07: the reset schedule section came out on Omar's call, so
     there is no interactive block left here at all. What survives is the
     handful of figures the page still speaks: the saving on the CTAs, and the
     sticky bar. Both come from ../../shared/vy-offer.js with
     window.VY_SURFACE = 'copy' set, so they are the numbers the destination
     actually charges rather than a different product's ladder.
     ============================================================ */

  const TIER_ORDER = ['b1g1', 'b2g2', 'b3g3'];
  const tubes = n => n + (n === 1 ? ' tube' : ' tubes');

  /* The one offer this page speaks for. Omar, 2026-08-07: Buy 1 Get 1 Free,
     which is `b2g2` on the copy ladder and also its TIER_DEFAULT, so the bar
     names the bar the reader actually lands on rather than one they have to go
     and find. Read off the module rather than hardcoded, so a repricing that
     moves the default moves this page with it. */
  const headline = () => O.TIER_DEFAULT;

  /* The saving on the offer the page is pointing at, never the best number
     available somewhere else on the ladder. Quoting the six tube saving over a
     two tube basket was the version of this that would have shipped a figure
     nobody could reconcile at checkout. */
  const headlineSave = () => {
    const t = O.TIERS[headline()];
    return Math.round((1 - O.tierTotal(headline(), O.SUB_DEFAULT) / t.compareCents) * 100);
  };

  const paintOffer = () => {
    const save = headlineSave(), k = headline(), t = O.TIERS[k];

    $$('[data-save-pct]').forEach(el => { el.textContent = save + '%'; });

    /* The closing card states the size it is quoting and what the percentage
       is measured against. A saving with no basket attached is the shape of
       claim a reader cannot check, and this one is checkable in one click.

       `compareCents` is tubes x the $79.95 LIST price, which is not what a
       single tube sells for on this ladder ($49.95). Calling it "the single
       tube price" would have overstated the anchor, so the card names it as a
       list price and prints the per tube figure the percentage comes from. */
    const rrp = t.compareCents / t.tubes, total = O.tierTotal(k, O.SUB_DEFAULT);
    /* On today's ladder the six tube total is exactly one tube's list price,
       which is worth saying plainly. Guarded rather than typed, because the
       client reprices this product and the sentence would quietly become
       false the day the two numbers stop matching. */
    $('#vk-offer-sub').textContent = total === rrp
      ? tubes(t.tubes) + ' for ' + money(total) + ', which is the list price of one.'
      : tubes(t.tubes) + ' for ' + money(total) + '. List price is ' + money(rrp) + ' a tube.';
    $$('[data-rrp]').forEach(el => { el.textContent = money(rrp); });

    /* Omar, 2026-08-07: NO price and NO percentage on either sticky bar. The
       bars follow the reader the whole way down the page, and a number that
       travels with them is a number they cannot check without leaving. Pricing
       is stated once, in the closing card, next to the basket it applies to.
       The bar names the offer and what it covers, both of which are facts of
       the ladder rather than figures. */
    /* The tube count is wrapped rather than concatenated so the phone can drop
       it: "2 tubes, Buy 1, Get 1 FREE" wraps to two lines at 390px next to the
       button, and the offer name alone is the part that carries the message. */
    $('#vk-bar-title').innerHTML =
      '<i class="vk-bar-count">' + esc(tubes(t.tubes)) + ', </i>' + esc(t.cardTitle);
    $('#vk-bar-sub').textContent   = t.cardSub.replace(/^\d+ tubes?\s*[.,·|-]?\s*/i, '') ||
      'the full growth cycle';
  };

  /* ---------- the destination ----------
     Written once here, and every CTA on the page is painted from it, so the
     thirteen links cannot drift apart. The client renamed this product once
     already and broke a live surface that had it hardcoded (omar-os #883),
     which is why the markup's own hrefs are only a no-JS fallback.

     WHY NOT O.PRODUCT_URL, which is what this used to read. The offer module
     points at /products/veyla™-hair-growth-serum, and that is a DIFFERENT
     PRODUCT (11108512694439) from the one the store sells on
     (10896017424551): the first carries the bundle ladder as variants and is
     the sandbox the lab PDP was built on, and pdp/vy-pdp.js deliberately
     rewrites links back to it. Pointing this lander at the module would send
     paid traffic to the sandbox.

     Omar, 2026-08-13: every CTA on this page goes to the real product page.
     A path, not an absolute url: same destination live, and it survives a
     preview domain. */
  const PDP = '/products/growth-serum';
  $$('[data-pdp]').forEach(a => { a.setAttribute('href', PDP); });

  paintOffer();

  /* ---------- sticky bar ----------
     There is no buybox to sit over any more, so it comes on once the reader is
     past the second block and stays on. It is a link, so there is nothing it
     can do by accident. */
  (() => {
    const bar = $('#vk-bar'), trigger = $('#b2');
    if (!bar || !trigger) return;
    const sync = past => bar.classList.toggle('is-on', past);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        sync(!e.isIntersecting && e.boundingClientRect.top < 0);
      }, { threshold: 0 }).observe(trigger);
    } else {
      const onScroll = () => sync(trigger.getBoundingClientRect().bottom < 0);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  })();

  /* ============================================================
     PART THREE: the two small widgets in the body copy
     ============================================================ */

  /* before and after, dragged. Pointer and keyboard both, because a comparison
     you can only reach with a mouse is a decoration. */
  (() => {
    const root = $('#vk-drag'); if (!root) return;
    const stage = root.querySelector('.vk-drag-stage');
    const b = root.querySelector('.vk-drag-b');
    const h = root.querySelector('.vk-drag-h');
    /* Not 50, and low rather than high. Two things decide this number.

       The customer's own week marker is printed across the middle top of her
       photographs, roughly 38% to 61% of the frame, so a centred divider lands
       straight through it and the reader's first impression is "We|ek 1".

       And `at` reveals the AFTER layer, so a high value opens the widget
       showing mostly the result. 67 looked right until the render showed two
       thirds of week twelve sitting there before anyone had touched it, which
       is the widget arguing on its own behalf. 33 clears the marker on the
       other side and opens on mostly BEFORE, which is the honest start: the
       reader drags to reveal the change rather than to hide it. */
    let at = 33;

    const set = v => {
      at = Math.max(0, Math.min(100, v));
      b.style.clipPath = 'inset(0 ' + (100 - at) + '% 0 0)';
      h.style.left  = at + '%';
      h.setAttribute('aria-valuenow', String(Math.round(at)));
    };
    const fromEvent = e => {
      const r = stage.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      set((x / r.width) * 100);
    };
    let dragging = false;
    const down = e => { dragging = true; fromEvent(e); };
    const move = e => { if (dragging) { fromEvent(e); if (e.cancelable) e.preventDefault(); } };
    const up   = () => { dragging = false; };

    stage.addEventListener('mousedown', down);
    stage.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    h.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  { set(at - 4); e.preventDefault(); }
      if (e.key === 'ArrowRight') { set(at + 4); e.preventDefault(); }
    });
    set(at);
    window.__VK_DRAG = { set, get at() { return at; } };
  })();

  /* the ponytail slider. Six months of a described change, which is a claim,
     so it is written as a description of what customers report rather than as
     a measurement, and it says so on screen. */
  (() => {
    const root = $('#vk-pony'); if (!root) return;
    const r = root.querySelector('input');
    const out = root.querySelector('.vk-pony-out');
    const bars = Array.prototype.slice.call(root.querySelectorAll('.vk-pony-vis i'));
    const SAY = [
      ['Today', 'The band goes round one more time than it used to and the ends are see through in daylight.'],
      ['Month 1', 'Nothing in the mirror yet. The scalp has stopped being itchy and tight, which is phase one doing its job.'],
      ['Month 2', 'The brush is lighter. This is the first read, and it is the one that tells you to keep going.'],
      ['Month 3', 'Short soft hairs along the part edge. Too short to style, long enough to see if you look for them.'],
      ['Month 4', 'The part starts reading narrower in photographs, which is the first change other people notice.'],
      ['Month 5', 'The ends stop being the thinnest part of it. Blow dried volume is the giveaway.'],
      ['Month 6', 'One fewer wrap of the band. Somebody asks whether you have had extensions put in.']
    ];
    const paintPony = () => {
      const v = Number(r.value);
      out.innerHTML = '<b>' + esc(SAY[v][0]) + '.</b> ' + esc(SAY[v][1]);
      bars.forEach((el, i) => {
        /* eight strands thickening across six months, described not measured */
        el.style.setProperty('--h', (34 + v * 7 + (i % 3) * 4) + '%');
        el.classList.toggle('is-on', i < 2 + v);
      });
    };
    r.addEventListener('input', paintPony);
    paintPony();
    window.__VK_PONY = { paint: paintPony };
  })();

  paintFiction();
  paintComments();

  /* exposed for the harness */
  window.__VK = { ago, SHOWN_FIRST, TIER_ORDER, headlineSave, headline, tubes };
})();
